// C1 — the shape behind the install verdict, drawn under the verify banner.
//
// IT LIVES HERE AND NOT ON THE ANALYTICS SCREEN, deliberately. "Daily volume by
// event type" is not a reading about readers; it is *is the pipe running*,
// which is the question `install_check` already answers in one sentence. On the
// analytics screen it would be a fourth axis nobody asked for; here it is the
// picture behind a sentence a customer is staring at while deciding whether
// their paste worked.
//
// THE ABSENT STATE DRAWS NOTHING. `InstallScreen.tsx` states the rule for the
// timestamp beside it — *"a dash standing in for one would read as a measured
// absence rather than an absent measurement"* — and a chart of zero-height
// bars is the same lie at greater size. An org that has never reported gets
// prose; so does one whose window happens to be empty.
//
// Tone stays neutral. Only `reporting` earns `nominal` on this screen: `quiet`
// and `never_reported` are statements about traffic, not about an install, and
// telling someone a working install is broken is the single most expensive
// thing this screen can do.

import { Card } from "../../ui";
import { StackedBars, type StackColumn } from "../../charts";
import type { ArrivalSeries } from "./api";
import "./ops.css";

const TYPE_LABELS: Record<string, string> = {
  session_start: "visits",
  section_view: "section views",
  term_hover: "term look-ups",
  term_dwell: "look-up dwell",
  term_rail_open: "rail opens",
};

/** Points -> one column per bucket, oldest first. Exported for the test: the
 *  bucketing is the contract, and asserting it through rendered `<rect>`s
 *  would pin the drawing instead. */
export function arrivalColumns(series: ArrivalSeries): StackColumn[] {
  const buckets = [...new Set(series.points.map((point) => point.bucket))].sort();
  return buckets.map((bucket) => ({
    key: bucket,
    label: bucket,
    slices: series.points
      .filter((point) => point.bucket === bucket)
      .map((point) => ({ key: point.type, value: point.events })),
  }));
}

export function ArrivalsChart({ series }: { series: ArrivalSeries }) {
  // Not one zero-height bar. See the header.
  if (series.points.length === 0) return null;

  const columns = arrivalColumns(series);
  const legend = series.types
    .map((type) => TYPE_LABELS[type] ?? type)
    .join(", ");

  return (
    <Card header="What we have received">
      <StackedBars
        label={
          `Events received per ${series.bucket} over the last` +
          ` ${series.window_hours} hours: ${series.total_events.toLocaleString("en-US")}` +
          ` in total, across ${columns.length} ${series.bucket}s, split by ${legend}`
        }
        columns={columns}
        keys={series.types}
      />
      <ul className="maisight-ops-legend type-numSm">
        {series.types.map((type, index) => (
          <li key={type}>
            <span
              className="maisight-ops-legend-swatch"
              data-series={index % 5}
              aria-hidden="true"
            />
            {TYPE_LABELS[type] ?? type}
          </li>
        ))}
      </ul>
      <p className="type-bodyS maisight-ops-card-note">
        Counted by when we received them, in UTC. Visits count sessions, never
        people.
      </p>
    </Card>
  );
}
